import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { TableCell, TableRow } from '@mui/material';
import TextTableCell from 'components/common/table/TextTableCell';
import { IconButton } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';
import useHistoryWithContext from 'components/menu/useHistoryWithContext';
import FlexBox from 'components/common/FlexBox';
import { useMutation, useSubscription } from 'hooks/apollo';
import { DELETE_LEASING_SERVICE_TYPE } from 'features/leasingServiceType/mutations';
import { LEASING_SERVICE_TYPE_DELETED } from 'features/leasingServiceType/subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_LEASING_SERVICE_TYPES_WRITE } from 'constants/rightsCodes';

const ItemComponent = ({ data }) => {
  const { t } = useTranslation();
  const historyContext = useHistoryWithContext();
  const addToast = useToast();
  const confirm = useConfirmDialog();

  const canWrite = usePermission(ADMIN_LEASING_SERVICE_TYPES_WRITE);

  const [pending, setPending] = useState(false);

  const handleEdit = useCallback(() => {
    historyContext.push(`/leasingServiceTypes/${data.id}`);
  }, [data, historyContext]);

  // #region Delete

  const [deleteLeasingServiceType] = useMutation(DELETE_LEASING_SERVICE_TYPE, {
    onError: _ => {
      setPending(false);
    }
  });

  const handleDelete = useCallback(() => {
    confirm(
      () => {
        setPending(true);
        deleteLeasingServiceType({
          variables: { id: data.id }
        });
      },
      {
        content: t('LeasingServiceType.ConfirmDelete', { name: data.name })
      }
    );
  }, [confirm, deleteLeasingServiceType, t, data]);

  useSubscription(LEASING_SERVICE_TYPE_DELETED, {
    skip: !data.id,
    variables: { id: data.id },
    onData: ({ client }) => {
      addToast(t('LeasingServiceType.Deleted'), 'success');
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'leasingServiceTypes' });
    }
  });

  // #endregion

  return (
    <TableRow>
      <TextTableCell content={data.code} />
      <TextTableCell content={data.name} />
      <TableCell>{data.categoryName}</TableCell>
      <TableCell>
        <FlexBox gap={0.5} justifyContent='end'>
          <IconButton
            type={canWrite ? 'edit' : 'view'}
            variant='text'
            color='primary'
            size='small'
            tooltip={canWrite ? t('Edit') : t('View')}
            onClick={handleEdit}
            disabled={pending}
          />
          {canWrite && (
            <IconButton
              type='delete'
              variant='text'
              color='primary'
              size='small'
              tooltip={t('Delete')}
              onClick={handleDelete}
              disabled={pending}
            />
          )}
        </FlexBox>
      </TableCell>
    </TableRow>
  );
};

ItemComponent.propTypes = {
  data: PropTypes.object
};

export default ItemComponent;
